import { IMAGE_MODELS } from '@/lib/ai-agent/model-constants';

export interface SelectRealismModelInput {
  referenceCount: number;
  realismTemplateId?: string;
  premium?: boolean;
  /**
   * True when any attached reference/anchor carries the `person` role.
   * Realism v2 §5.5 — identity-critical generations (a face/person ref) MUST
   * route to Nano Banana Pro (`gemini-3-pro-image`) even for a single
   * reference, because Flash drifts on faces across generations. Without this,
   * a lone selfie/face ref fell through to Flash (referenceCount 1 < 2, no
   * template) and identity preservation degraded — the reason "Nano Banana Pro
   * isn't being used" for person work.
   */
  hasPersonReference?: boolean;
  /**
   * True when any attached reference/anchor carries the `product` (or object)
   * role. A customer uploading THEIR product expects it reproduced exactly and
   * consistently across generations — verified live (2026-06-25) that Nano
   * Banana Pro preserves product geometry/material/branding noticeably tighter
   * than Flash on a single product ref. A lone product ref (referenceCount 1, no
   * person, no template, not premium) otherwise fell through to Flash and drifted
   * on fine product detail. Treat product fidelity like identity: route to Pro.
   */
  hasProductReference?: boolean;
  /**
   * True when any attached reference/anchor carries the `logo` role. A brand
   * logo is a fidelity-critical mark: Flash drifts wordmark geometry/kerning
   * on a single ref (letterforms warp, colors shift), while Nano Banana Pro
   * holds marks tighter — same rationale class as products (BI-4 research
   * 2026-07-02). Applies to `auto` model selection ONLY: unlike the person
   * guardrail, there is no airtight override here — an explicit/pinned
   * `imageModel` still wins downstream, so BI-4-05's A/B proof runs stay clean.
   */
  hasLogoReference?: boolean;
  /**
   * Cost-saving posture (see `isImageCostSavingModeEnabled` in
   * `src/lib/feature-flags.ts`). When true, the logo-only and "2+ generic
   * references" catch-alls route to Flash instead of Pro. Identity (person),
   * product-fidelity, explicit `premium`, and `realismTemplateId` routing are
   * NOT affected — those remain Pro regardless, since they're either safety
   * guardrails or an explicit quality request.
   */
  costSavingMode?: boolean;
}

export type SelectableImageModel = typeof IMAGE_MODELS.FLASH | typeof IMAGE_MODELS.PRO;

export function selectRealismModel(input: SelectRealismModelInput): SelectableImageModel {
  const {
    referenceCount,
    realismTemplateId,
    premium,
    hasPersonReference,
    hasProductReference,
    hasLogoReference,
    costSavingMode,
  } = input;

  if (referenceCount === 0) {
    // Text-only generations — single OR batch/premium — route to Nano Banana 2
    // (Gemini Flash Image). The legacy Imagen 4 family
    // (imagen-4.0-*-generate-001) is retired: Google discontinues those predict
    // endpoints on 2026-08-17, and they are not on Mora's frontier-only image
    // model list. Flash supports native `n` batching via the Vercel AI SDK.
    return IMAGE_MODELS.FLASH;
  }

  // Identity-critical: a person/face ref always routes to Nano Banana Pro.
  if (hasPersonReference) return IMAGE_MODELS.PRO;
  // Fidelity-critical: a customer's own product ref also routes to Pro so the
  // uploaded object is reproduced exactly + consistently (verified live).
  if (hasProductReference) return IMAGE_MODELS.PRO;
  // Fidelity-critical: a brand logo mark also routes to Pro (auto-mode only —
  // no airtight override, see hasLogoReference doc comment above) — unless
  // cost-saving mode is on, which relaxes this lower-stakes catch-all.
  if (hasLogoReference && !costSavingMode) return IMAGE_MODELS.PRO;
  if (realismTemplateId) return IMAGE_MODELS.PRO;
  // Explicit premium request always honored (user opt-in), independent of
  // cost-saving mode. The generic "2+ references" catch-all is relaxed under
  // cost-saving mode instead.
  if (premium) return IMAGE_MODELS.PRO;
  if (referenceCount >= 2 && !costSavingMode) return IMAGE_MODELS.PRO;
  return IMAGE_MODELS.FLASH;
}

export function enforcePersonReferenceModel<T extends string>(
  model: T,
  hasPersonReference: boolean,
): { model: T | typeof IMAGE_MODELS.PRO; upgraded: boolean } {
  if (hasPersonReference && model !== IMAGE_MODELS.PRO) {
    return { model: IMAGE_MODELS.PRO, upgraded: true };
  }
  return { model, upgraded: false };
}
